import { useEffect, useMemo, useState } from "react";
import { EyeOff, LayoutGrid, Rows3, Star } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { dismissGraphVenue, restoreGraphVenue, saveGraphVenue, type GraphVenue } from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PitchSheet from "./PitchSheet";
import VenueEvidenceSheet from "./VenueEvidenceSheet";
import { formatContactedRecency, formatLocation, friendlyError } from "./discoverCopy";

type Props = {
  artistId: string;
  venues: GraphVenue[];
  comparableCount: number;
  loading: boolean;
  error: string | null;
  artistFilter: { id: number; name: string } | null;
  onClearArtistFilter: () => void;
  onVenueSaved: (globalVenueId: string) => void;
  onVenuePitchState: (globalVenueId: string, state: "prospect" | "contacted") => void;
  onVenueDismissal: (globalVenueId: string, privateState: GraphVenue["private_state"]) => void;
  onGoToArtists: () => void;
};

/** The active artist's private relationship with a venue, from existing artist_venues state only. */
export type VenueStatus = "new" | "saved" | "prospect" | "contacted";

const STATUS_LABELS: Record<VenueStatus, string> = {
  new: "New",
  saved: "Saved",
  prospect: "Prospect",
  contacted: "Contacted",
};

export function venueStatus(venue: GraphVenue): VenueStatus {
  const status = (venue.private_state.status ?? "").toLowerCase();
  if (status === "contacted") return "contacted";
  if (status === "prospect") return "prospect";
  return venue.private_state.saved ? "saved" : "new";
}

/** Region filter key: state/region when known, otherwise country. */
const regionOf = (venue: GraphVenue): string | null => venue.region?.trim() || venue.country?.trim() || null;

const selectClass = "mt-1 rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground";

/** `?view=table` / `?view=cards`; Cards is the default. A future Map mode belongs here too. */
type ViewMode = "cards" | "table";
const parseView = (value: string | null): ViewMode => (value === "table" ? "table" : "cards");

export default function DiscoverVenuesPanel({
  artistId,
  venues,
  comparableCount,
  loading,
  error,
  artistFilter,
  onClearArtistFilter,
  onVenueSaved,
  onVenuePitchState,
  onVenueDismissal,
  onGoToArtists,
}: Props) {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [comparableId, setComparableId] = useState(artistFilter ? String(artistFilter.id) : "all");
  const [status, setStatus] = useState<"all" | VenueStatus>("all");
  const [minArtists, setMinArtists] = useState(1);
  const [showDismissed, setShowDismissed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"));
  const changeView = (next: ViewMode) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", next);
        return params;
      },
      { replace: true },
    );
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [openVenue, setOpenVenue] = useState<GraphVenue | null>(null);
  const [pitchVenue, setPitchVenue] = useState<GraphVenue | null>(null);

  // "View venues" on a comparable artist arrives as a filter from the Artists tab.
  useEffect(() => {
    setComparableId(artistFilter ? String(artistFilter.id) : "all");
  }, [artistFilter]);

  const regionOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const venue of venues) {
      const key = regionOf(venue);
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [venues]);

  const comparableOptions = useMemo(() => {
    const names = new Map<number, string>();
    for (const venue of venues) for (const artist of venue.comparable_artists) names.set(artist.public_artist_id, artist.name);
    if (artistFilter) names.set(artistFilter.id, artistFilter.name);
    return [...names.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [venues, artistFilter]);

  const maxObserved = useMemo(() => Math.max(1, ...venues.map((v) => v.comparable_artist_count)), [venues]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return venues.filter((venue) => {
      if (venue.private_state.dismissed && !showDismissed) return false;
      if (comparableId !== "all" && !venue.comparable_artists.some((a) => String(a.public_artist_id) === comparableId)) return false;
      if (region !== "all" && regionOf(venue) !== region) return false;
      if (status !== "all" && venueStatus(venue) !== status) return false;
      if (venue.comparable_artist_count < minArtists) return false;
      if (term) {
        const haystack = [venue.name, venue.city, venue.region, venue.country].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [venues, query, region, comparableId, status, minArtists, showDismissed]);

  const dismissedCount = useMemo(() => venues.filter((venue) => venue.private_state.dismissed).length, [venues]);
  const filtersActive =
    Boolean(query.trim()) || region !== "all" || comparableId !== "all" || status !== "all" || minArtists > 1 || showDismissed;
  const clearFilters = () => {
    setShowDismissed(false);
    setQuery("");
    setRegion("all");
    setStatus("all");
    setMinArtists(1);
    setComparableId("all");
    if (artistFilter) onClearArtistFilter();
  };
  const handleComparableChange = (value: string) => {
    setComparableId(value);
    if (artistFilter && value !== String(artistFilter.id)) onClearArtistFilter();
  };

  const handleDismissal = async (venue: GraphVenue, dismiss: boolean) => {
    if (!venue.global_venue_id) return;
    const id = venue.global_venue_id;
    setDismissingId(id);
    setSaveErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const response = dismiss ? await dismissGraphVenue(id, artistId) : await restoreGraphVenue(id, artistId);
      onVenueDismissal(id, response.private_state);
    } catch (err) {
      const fallback = dismiss ? "Couldn't hide this venue. Please try again." : "Couldn't restore this venue. Please try again.";
      setSaveErrors((prev) => ({ ...prev, [id]: friendlyError(err, fallback) }));
    } finally {
      setDismissingId(null);
    }
  };

  const handleSave = async (venue: GraphVenue) => {
    if (!venue.global_venue_id) return;
    const id = venue.global_venue_id;
    setSavingId(id);
    setSaveErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      await saveGraphVenue(id, artistId);
      onVenueSaved(id);
    } catch (err) {
      setSaveErrors((prev) => ({ ...prev, [id]: friendlyError(err, "Couldn't save this venue. Please try again.") }));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading venues…</p>;
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  if (comparableCount === 0) {
    return (
      <div className="aa-glass-card rounded-2xl p-6 text-center">
        <p className="font-medium text-foreground">Add comparable artists to see venues.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll show you where artists like you have actually played.
        </p>
        <Button className="mt-4" onClick={onGoToArtists}>
          Choose comparable artists
        </Button>
      </div>
    );
  }
  if (venues.length === 0) {
    return (
      <div className="aa-glass-card rounded-2xl p-6 text-center">
        <p className="font-medium text-foreground">No venues yet.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Use “Find their venues” on your comparable artists to load where they've played.
        </p>
        <Button className="mt-4" variant="secondary" onClick={onGoToArtists}>
          Go to comparable artists
        </Button>
      </div>
    );
  }

  const locationOf = (venue: GraphVenue) =>
    formatLocation([venue.city, venue.region]) || formatLocation([venue.country]) || "Location unknown";
  const canPitch = (venue: GraphVenue) => Boolean(venue.global_venue_id) && venue.pitchable !== false;

  /** Prospect/Contacted text shown beside Pitch; Saved is carried by the star. */
  const pipelineLabel = (venue: GraphVenue): string | null => {
    if (venue.identity?.status === "needs_review") return null;
    const state = venueStatus(venue);
    if (state === "contacted") {
      return venue.private_state.contacted_at ? formatContactedRecency(venue.private_state.contacted_at) : "Contacted";
    }
    return state === "prospect" ? "Prospect" : null;
  };

  const statusText = (venue: GraphVenue): string => {
    const parts: string[] = [];
    if (venue.identity?.status === "needs_review") parts.push("Needs review");
    parts.push(STATUS_LABELS[venueStatus(venue)]);
    if (venue.private_state.dismissed) parts.push("Dismissed");
    return parts.join(" · ");
  };

  /**
   * Save control. There is no unsave API yet, so a saved venue shows a filled,
   * non-interactive star instead of pretending to toggle.
   */
  const renderStar = (venue: GraphVenue) => {
    if (!canPitch(venue)) return null;
    if (venue.private_state.saved) {
      return (
        <span
          role="img"
          aria-label="Saved venue"
          title="Saved"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center text-amber-500"
        >
          <Star className="h-4 w-4" fill="currentColor" aria-hidden="true" />
        </span>
      );
    }
    const saving = savingId === venue.global_venue_id;
    return (
      <button
        type="button"
        aria-label="Save venue"
        title="Save venue"
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          void handleSave(venue);
        }}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        <Star className="h-4 w-4" fill="none" aria-hidden="true" />
      </button>
    );
  };

  const renderPitch = (venue: GraphVenue) =>
    canPitch(venue) ? (
      <Button size="sm" className="h-7 px-3 text-xs" onClick={() => setPitchVenue(venue)} aria-label={`Pitch ${venue.name}`}>
        Pitch
      </Button>
    ) : null;

  const renderEvidence = (venue: GraphVenue, inTable = false) => (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={`Comparable artists (${venue.comparable_artist_count})`}
      className="text-xs font-medium text-primary hover:underline focus-visible:underline focus-visible:outline-none"
      onClick={() => setOpenVenue(venue)}
    >
      {inTable ? venue.comparable_artist_count : `Comparable artists (${venue.comparable_artist_count})`}
    </button>
  );

  const renderHideRestore = (venue: GraphVenue) => {
    const state = venueStatus(venue);
    const busy = dismissingId === venue.global_venue_id;
    if (venue.private_state.dismissed) {
      return (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => void handleDismissal(venue, false)}
          disabled={busy}
          aria-label={`Restore ${venue.name}`}
        >
          {busy ? "Restoring…" : "Restore"}
        </Button>
      );
    }
    // Prospect/Contacted carry booking history and are managed from Booking, not dismissed here.
    if (!venue.global_venue_id || (state !== "new" && state !== "saved")) return null;
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        onClick={() => void handleDismissal(venue, true)}
        disabled={busy}
        aria-label={`Dismiss ${venue.name}`}
        title="Hide from my Discover results (you can restore it later)"
      >
        <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
        Hide
      </button>
    );
  };

  return (
    <section className="space-y-3" aria-label="Venues">
      <div className="flex flex-wrap items-end gap-3" role="group" aria-label="Filter venues">
        <div className="min-w-[180px] flex-1">
          <Input
            placeholder="Search venue or city"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search venues"
          />
        </div>
        <label className="flex flex-col text-xs text-muted-foreground">
          State / region
          <select className={selectClass} value={region} onChange={(event) => setRegion(event.target.value)} aria-label="State or region">
            <option value="all">All</option>
            {regionOptions.map(([value, count]) => (
              <option key={value} value={value}>
                {value} ({count})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Comparable artist
          <select
            className={selectClass}
            value={comparableId}
            onChange={(event) => handleComparableChange(event.target.value)}
            aria-label="Comparable artist"
          >
            <option value="all">All</option>
            {comparableOptions.map(([id, name]) => (
              <option key={id} value={String(id)}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Status
          <select
            className={selectClass}
            value={status}
            onChange={(event) => setStatus(event.target.value as "all" | VenueStatus)}
            aria-label="Venue status"
          >
            <option value="all">All</option>
            {(Object.keys(STATUS_LABELS) as VenueStatus[]).map((key) => (
              <option key={key} value={key}>
                {STATUS_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 self-end pb-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border border-border"
            checked={showDismissed}
            onChange={(event) => setShowDismissed(event.target.checked)}
          />
          Show dismissed ({dismissedCount})
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Played by
          <select
            className={selectClass}
            value={minArtists}
            onChange={(event) => setMinArtists(Number(event.target.value))}
            aria-label="Minimum comparable artists"
          >
            {Array.from({ length: maxObserved }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n === 1 ? "Any" : `${n}+ artists`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <p role="status" aria-live="polite">
          Showing {filtered.length} of {venues.length} venues
          {!showDismissed && dismissedCount > 0 ? ` · ${dismissedCount} dismissed hidden` : ""}
        </p>
        {filtersActive && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <div className="ml-auto inline-flex rounded-md border border-border p-0.5" role="group" aria-label="Display mode">
          {(
            [
              ["cards", "Cards", LayoutGrid],
              ["table", "Table", Rows3],
            ] as const
          ).map(([mode, label, Icon]) => (
            <button
              key={mode}
              type="button"
              aria-pressed={view === mode}
              onClick={() => changeView(mode)}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                view === mode ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && <p className="text-sm text-muted-foreground">No venues match these filters.</p>}

      {view === "cards" ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-2">
          {filtered.map((venue, index) => {
            const key = venue.global_venue_id ?? `venue-${venue.venue_ids.join("-") || index}`;
            const dismissed = Boolean(venue.private_state.dismissed);
            const needsReview = venue.identity?.status === "needs_review";
            const saveError = venue.global_venue_id ? saveErrors[venue.global_venue_id] : undefined;
            return (
              <article
                key={key}
                className={`aa-glass-card flex flex-col gap-2 rounded-xl p-3 ${dismissed ? "opacity-75" : ""}`}
                aria-label={venue.name}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground" title={venue.name}>
                      {venue.name}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">{locationOf(venue)}</p>
                  </div>
                  {renderStar(venue)}
                </div>

                {dismissed && (
                  <p className="text-[11px] font-medium text-muted-foreground">
                    <EyeOff className="mr-1 inline h-3 w-3 align-[-2px]" aria-hidden="true" />
                    Dismissed · hidden from your normal results
                  </p>
                )}

                {needsReview && (
                  <p className="text-[11px] leading-snug text-amber-600 dark:text-amber-300">
                    <span className="font-semibold">Needs review</span> ·{" "}
                    {venue.identity.reason ?? "This venue's identity needs review."} It can't be saved or pitched yet.
                  </p>
                )}

                {(canPitch(venue) || pipelineLabel(venue)) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {renderPitch(venue)}
                    {pipelineLabel(venue) && <span className="text-[11px] font-medium text-muted-foreground">{pipelineLabel(venue)}</span>}
                  </div>
                )}
                {!venue.global_venue_id && (
                  <p className="text-[11px] text-muted-foreground" title="This venue can't be saved yet.">
                    Can't save yet
                  </p>
                )}

                <div className="mt-auto flex items-center justify-between gap-2">
                  {renderEvidence(venue)}
                  {renderHideRestore(venue)}
                </div>
                {saveError && (
                  <p role="alert" className="text-xs text-destructive">
                    {saveError}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <Table aria-label="Venues">
            <TableHeader>
              <TableRow>
                <TableHead>Venue</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Comparable artists</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last contacted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((venue, index) => {
                const key = venue.global_venue_id ?? `venue-${venue.venue_ids.join("-") || index}`;
                const saveError = venue.global_venue_id ? saveErrors[venue.global_venue_id] : undefined;
                return (
                  <TableRow key={key} aria-label={venue.name} className={venue.private_state.dismissed ? "opacity-75" : ""}>
                    <TableCell className="max-w-[16rem]">
                      <span className="block truncate font-medium text-foreground" title={venue.name}>
                        {venue.name}
                      </span>
                      {saveError && (
                        <span role="alert" className="block text-xs text-destructive">
                          {saveError}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{locationOf(venue)}</TableCell>
                    <TableCell>{renderEvidence(venue, true)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{statusText(venue)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {venue.private_state.contacted_at ? (
                        <span title={venue.private_state.contacted_at}>
                          {formatContactedRecency(venue.private_state.contacted_at).replace(/^Contacted /, "")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        {renderStar(venue)}
                        {renderPitch(venue)}
                        {renderHideRestore(venue)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <VenueEvidenceSheet venue={openVenue} onClose={() => setOpenVenue(null)} />
      <PitchSheet
        artistId={artistId}
        globalVenueId={pitchVenue?.global_venue_id ?? null}
        venueName={pitchVenue?.name}
        onClose={() => setPitchVenue(null)}
        onStateChange={onVenuePitchState}
      />
    </section>
  );
}
